import { rgba } from './utils';
import { baseTheme } from './baseTheme';
import { GRAY_SCALE } from 'constants/ThemeConstant';

export const getBaseComponentThemeConfig = (
    {
        Button = {},
        Menu = {},
        Card = {},
        Table = {},
        Select = {},
        DatePicker = {},
        TimePicker = {},
        Layout = {},
        Input = {},
        InputNumber = {},
        Checkbox = {},
        Dropdown = {},
        Calendar = {},
        Radio = {},
        Switch = {},
        Modal = {},
        Drawer = {},
        Popover = {},
        Breadcrumb = {},
        Pagination = {},
        Segmented = {},
        Steps = {},
        Tabs = {},
    } = {},
    primaryColor = baseTheme.colorPrimary
) => {
    /** Modal / Modal.confirm / Popconfirm — ưu tiên surface Card, rồi Input, rồi Layout (dark mode) */
    const modalSurfaceBg =
        Modal.contentBg ??
        Card.colorBgContainer ??
        Card.headerBg ??
        Input.colorBgContainer ??
        Layout.headerBg ??
        GRAY_SCALE.WHITE;

    const modalTitleColor = Modal.titleColor ?? Card.colorTextHeading;
    const modalTextColor = Modal.colorText ?? Card.colorText;

    return {
    Button: {
            borderRadius: baseTheme.borderRadius,
            borderRadiusSM: baseTheme.borderRadius,
            borderRadiusLG: baseTheme.borderRadius,
            defaultBorderColor: 'rgba(255,255,255,.15)',
            defaultShadow: 'none',
            ...Button
        },
    Dropdown: {
        controlPaddingHorizontal: 20,
        controlHeight: 37,
        borderRadiusLG: 10,
        paddingXXS: '4px 0px',
        ...Dropdown
    },

    Calendar: {
        fullBg: 'transparent',
        fullPanelBg: 'transparent',
    
        itemActiveBg: rgba(
            baseTheme.colorPrimary,
            0.15
        ),
    
        ...Calendar
    },

    Card: {
        paddingLG: 20,
        ...Card
    },

    Layout,

    Menu: {
        itemPaddingInline: 12,
        itemHeight: 38,
    
        itemBg: 'transparent',
        colorActiveBarHeight: 0,
    
        itemSelectedColor: baseTheme.colorPrimary,
        itemHoverColor: baseTheme.colorPrimary,
    
        itemHoverBg: rgba(baseTheme.colorPrimary, 0.06),
        itemBgSelected: rgba(baseTheme.colorPrimary, 0.1),
    
        ...Menu
    },

    Input: {
        activeBorderColor: baseTheme.colorPrimary,
        hoverBorderColor: baseTheme.colorPrimary,
    
        activeShadow: `0 0 0 2px ${rgba(
            baseTheme.colorPrimary,
            0.15
        )}`,
    
        // size middle / large / small map tới 3 token khác nhau
        borderRadius: 22,
        borderRadiusSM: 22,
        borderRadiusLG: 22,
    
        ...Input
    },

    // Ant Design tách Input vs InputNumber — kế thừa override của Input nếu theme không khai báo riêng
    InputNumber: {
        activeBorderColor: baseTheme.colorPrimary,
        hoverBorderColor: baseTheme.colorPrimary,
        activeShadow: `0 0 0 2px ${rgba(
            baseTheme.colorPrimary,
            0.15
        )}`,
        borderRadius: 22,
        borderRadiusSM: 22,
        borderRadiusLG: 22,
        ...Input,
        ...InputNumber,
    },
    Checkbox: {
        // Nền chưa check: cùng màu Input (tránh ăn colorBgContainer global của theme, vd. nền kem Tet)
        colorBgContainer: Input.colorBgContainer ?? GRAY_SCALE.WHITE,
        ...(Input.colorBorder ? { colorBorder: Input.colorBorder } : {}),
        activeBorderColor: Input.activeBorderColor ?? baseTheme.colorPrimary,
        hoverBorderColor: Input.hoverBorderColor ?? baseTheme.colorPrimary,
        activeShadow: `0 0 0 2px ${rgba(
            baseTheme.colorPrimary,
            0.15
        )}`,
        borderRadius: 2,
        ...Checkbox,
    },
    Select: {
        controlHeight: 40,
        controlItemBgActive: rgba(
            baseTheme.colorPrimary,
            0.1
        ),
        borderRadius: 22,
        borderRadiusSM: 22,
        borderRadiusLG: 22,
        ...Select
    },

    DatePicker,
    TimePicker,
    Table,
  
    Pagination: {
        itemActiveBg: primaryColor,
        colorPrimary: GRAY_SCALE.WHITE,

        ...Pagination},
    Segmented: {
        itemSelectedBg: primaryColor,
        itemSelectedColor: GRAY_SCALE.WHITE,

        ...Segmented},
    Breadcrumb: {
        itemColor: GRAY_SCALE.GRAY,
        lastItemColor: baseTheme.colorPrimary,
        separatorColor: GRAY_SCALE.GRAY,      
        ...Breadcrumb 
    },
    Steps: {
        colorTextLightSolid: GRAY_SCALE.WHITE,
        ...Steps,
    },
    Radio: {
        colorBgContainer: Input.colorBgContainer ?? GRAY_SCALE.WHITE,
        ...(Input.colorBorder ? { colorBorder: Input.colorBorder } : {}),
        activeBorderColor: Input.activeBorderColor ?? baseTheme.colorPrimary,
        hoverBorderColor: Input.hoverBorderColor ?? baseTheme.colorPrimary,
        activeShadow: `0 0 0 2px ${rgba(
            baseTheme.colorPrimary,
            0.15
        )}`,
        borderRadius: 2,
        ...Radio,
    },
    Switch: {
        colorBgContainer: Input.colorBgContainer ?? GRAY_SCALE.WHITE,
        ...(Input.colorBorder ? { colorBorder: Input.colorBorder } : {}),
        activeBorderColor: Input.activeBorderColor ?? baseTheme.colorPrimary,
        hoverBorderColor: Input.hoverBorderColor ?? baseTheme.colorPrimary,
        activeShadow: `0 0 0 2px ${rgba(
            baseTheme.colorPrimary,
            0.15
        )}`,
        borderRadius: 2,
        ...Switch,
    },
    Modal: {
        contentBg: modalSurfaceBg,
        headerBg: Modal.headerBg ?? modalSurfaceBg,
        footerBg: Modal.footerBg ?? modalSurfaceBg,
        ...(modalTitleColor ? { titleColor: modalTitleColor } : {}),
        ...(modalTextColor ? { colorText: modalTextColor } : {}),
        ...Modal,
    },
    Drawer: {
        colorBgElevated: Drawer.colorBgElevated ?? modalSurfaceBg,
        ...(modalTextColor ? { colorText: modalTextColor } : {}),
        ...(modalTitleColor ? { colorTextHeading: modalTitleColor } : {}),
        ...Drawer,
    },
    /** Popconfirm dùng Popover — đồng bộ nền với Modal */
    Popover: {
        colorBgElevated: Popover.colorBgElevated ?? modalSurfaceBg,
        ...(modalTextColor ? { colorText: modalTextColor } : {}),
        ...Popover,
    },
    Tabs: {
        ...Tabs,
    },
};
};

export const createTheme = ({
    primaryColor,
    successColor,
    warningColor,
    errorColor,

    bgColor,
    textColor,
    borderColor,
    bodyBg,
    colorBgLayout,
    colorBgElevated,

    moduleMenuBg,
    moduleMenuIconColor,
    moduleMenuItemColor,
    moduleMenuItemSelectedBg,
    moduleMenuItemSelectedColor,
    moduleMenuItemSelectedBorderColor,

    menuGroupTitleColor,
    headerNavColor,
    headerNavBorderColor,

    siderBg,
    customMenuBg,
    /** 'dark' | 'light' — Ant Design Menu theme cho `.custom-menu` (mặc định theo sideNav) */
    customMenuAntTheme,

    customMenuItemSelectedColor,
    customMenuItemSelectedBg,
    customMenuItemSelectedIconColor,
    customMenuItemSelectedBorderColor,

    footerBg,
    footerTextColor,
    footerBorderColor,

    colorTextDescription,
    colorTextHeading,

    componentOverrides = {}
}) => ({
    token: {
        ...baseTheme,

        colorPrimary: primaryColor,
        colorSuccess: successColor,
        colorWarning: warningColor,
        colorError: errorColor,

        colorTextBase: textColor,
        colorText: textColor,
        ...(colorTextDescription ? { colorTextDescription } : {}),
        ...(colorTextHeading ? { colorTextHeading } : {}),

        colorBorder: borderColor,
        colorBgBase: bgColor,
        colorBgLayout: colorBgLayout ?? bodyBg ?? bgColor,
        ...(colorBgElevated ? { colorBgElevated } : {}),
        bodyBg: bodyBg ?? bgColor,

        moduleMenuBg,
        moduleMenuIconColor,
        moduleMenuItemColor,
        moduleMenuItemSelectedBg,
        moduleMenuItemSelectedColor,
        ...(moduleMenuItemSelectedBorderColor ? { moduleMenuItemSelectedBorderColor } : {}),

        menuGroupTitleColor: menuGroupTitleColor ?? rgba(primaryColor, 0.9),

        headerNavColor,
        ...(headerNavBorderColor ? { headerNavBorderColor } : {}),

        siderBg,
        customMenuBg,
        ...(customMenuAntTheme ? { customMenuAntTheme } : {}),

        customMenuItemSelectedColor,
        customMenuItemSelectedBg,
        customMenuItemSelectedIconColor,
        customMenuItemSelectedBorderColor,

        footerBg,
        footerTextColor,
        footerBorderColor,
    },

    components: getBaseComponentThemeConfig(
        componentOverrides,
        primaryColor
    )
});